"use client"

import Text from "@/components/common/Typography/Text"
import { useIsDesktop } from "@/hooks/app/useIsDesktop"
import { useTour } from "@/hooks/tour/useTour"
import { Image } from "antd"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

const TourDetailImages = () => {
  const { tour: { data: tour } } = useTour()
  const isDesktop = useIsDesktop()
  const { t } = useTranslation()

  const images = useMemo(() => {
    const gallery = (tour?.images || []).map(img => img.path)
    const itineraryImages = (tour.itinerary || []).reduce(
      (arr: string[], item) => arr.concat(item?.images || []),
      []
    )
    return [...gallery, ...itineraryImages].filter(Boolean)
  }, [tour?.images, tour.itinerary])

  const displayImages = useMemo(() => {
    if (!images.length) return []
    const padded = [...images]
    while (padded.length < 5) {
      padded.push(padded[0])
    }
    return padded.slice(0, 5)
  }, [images])

  const airline = useMemo(() => {
    const parts = (tour?.name || "").split("|").map((s) => s.trim()).filter(Boolean)
    return parts.length < 2 ? "" : parts[parts.length - 1]
  }, [tour?.name])

  if (!displayImages.length) {
    return null
  }

  const [hero, ...gridImages] = displayImages
  const extraCount = Math.max(0, images.length - 5)

  const renderCell = (src: string, alt: string, showMore = false) => (
    <div className="tour-detail__gallery-cell">
      <Image
        src={src}
        alt={alt}
        preview
        style={{ width: "100%", height: "100%", objectFit: "cover" }}
      />
      {showMore && extraCount > 0 && (
        <div className="tour-detail__gallery-more">
          <Text strong className="tour-detail__gallery-more-label">
            +{extraCount} {t("tour.view_all_photos")}
          </Text>
        </div>
      )}
    </div>
  )

  return (
    <Image.PreviewGroup>
      <div
        className={`tour-detail__gallery-grid${isDesktop ? "" : " tour-detail__gallery-grid--mobile"}`}
      >
          <div className="tour-detail__gallery-cell tour-detail__gallery-cell--hero">
            <Image
              src={hero}
              alt={tour?.name || "tour"}
              preview
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
            {(airline || tour?.providerName) && (
              <span className="tour-item__airline-badge">
                {airline || tour?.providerName}
              </span>
            )}
          </div>
          {gridImages.map((src, index) => (
            <div key={`${src}-${index}`} className="tour-detail__gallery-cell">
              {renderCell(
                src,
                "",
                index === gridImages.length - 1 && images.length > 5
              )}
            </div>
          ))}
        </div>
      {images.slice(5).map(src => (
        <Image key={`extra-${src}`} src={src} alt="" style={{ display: "none" }} />
      ))}
    </Image.PreviewGroup>
  )
}

export default TourDetailImages
